/**
 * Insurance picker — extracted from `BookingWidget.jsx`. Renders the
 * "Minimum coverage" radio (always shown, even when the vendor has no
 * upsell plans), the ⓘ popover trigger, and one row per vendor-defined
 * insurance plan. The popover's "Upgrade to {plan}" CTA calls back into
 * `setPlanId` to auto-select the cheapest plan.
 *
 * Iter 67ah — each row now surfaces the deposit amount tied to that
 * coverage level so renters can compare ("€500 deposit" vs "Free deposit").
 * Deposit comes from `plan.deposit` for upsell plans and from `car.deposit`
 * for the baseline Minimum Coverage row.
 */
import { useTranslation } from "react-i18next";
import Money from "@/components/Money";
import { ShieldCheck, Wallet, Check, X } from "lucide-react";
import { InsuranceCoveragePopover } from "@/components/car/InsuranceCoveragePopover";

function DepositChip({ amount, testId }) {
  // 0 (or unset) → "Free deposit" amber-on-emerald pill; >0 → muted neutral pill.
  const num = Number(amount) || 0;
  if (num <= 0) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em]"
        data-testid={testId}
      >
        <Wallet className="h-3 w-3" /> Free deposit
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-neutral-100 text-neutral-700 border border-neutral-200 px-2 py-0.5 text-[10px] font-medium"
      data-testid={testId}
    >
      <Wallet className="h-3 w-3" /> <Money amount={num} className="font-semibold" /> <span className="opacity-70">deposit</span>
    </span>
  );
}

export function BookingInsurancePicker({ car, planId, setPlanId }) {
  const { t } = useTranslation();
  const plans = car.insurance_plans || [];
  const minDeposit = Number(car.deposit) || 0;

  return (
    <div className="space-y-2 mb-3" data-testid="booking-insurance-picker">
      <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold flex items-center gap-1.5">
        <ShieldCheck className="h-3 w-3 text-emerald-600" aria-hidden="true" />
        {t("bookingWidget.insuranceTitle")}
      </div>

      <div
        className={`w-full rounded-xl border px-3 py-2.5 transition flex items-center justify-between gap-2 ${planId === "" ? "border-blue-600 bg-blue-50" : "border-border hover:bg-neutral-50"}`}
        data-testid="insurance-option-none"
      >
        <button
          type="button"
          onClick={() => setPlanId("")}
          className="flex-1 text-left min-w-0"
        >
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-sm font-medium">{t("bookingWidget.noInsurance")}</span>
            <DepositChip amount={minDeposit} testId="insurance-option-none-deposit" />
          </div>
          <div className="text-xs text-neutral-500">{t("bookingWidget.skipCoverage")}</div>
        </button>
        <InsuranceCoveragePopover car={car} onUpgrade={(id) => setPlanId(id)} />
        <button
          type="button"
          onClick={() => setPlanId("")}
          className="text-sm text-neutral-500 ml-2 shrink-0"
          tabIndex={-1}
        >
          <Money amount={0} />
        </button>
      </div>

      {plans.map((p) => {
        const covered = Array.isArray(p.coverage) ? p.coverage.filter(Boolean) : [];
        const excluded = Array.isArray(p.exclusions) ? p.exclusions.filter(Boolean) : [];
        return (
          <div
            key={p.id}
            className={`rounded-xl border transition overflow-hidden ${planId === p.id ? "border-blue-600 ring-1 ring-blue-300" : "border-border"}`}
            data-testid={`insurance-option-${p.id}`}
          >
            <button
              type="button"
              onClick={() => setPlanId(p.id)}
              className={`w-full text-left px-3 py-2.5 flex items-center justify-between gap-2 ${planId === p.id ? "bg-blue-50" : "hover:bg-neutral-50"}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-sm font-medium truncate">{p.name}</span>
                  <DepositChip amount={p.deposit} testId={`insurance-option-${p.id}-deposit`} />
                </div>
                {p.description && <div className="text-xs text-neutral-500 truncate">{p.description}</div>}
              </div>
              <div className="text-sm font-medium shrink-0 ml-2"><Money amount={p.price_per_day} />/day</div>
            </button>
            {/* Iter 67bu — Coverage detail rows. Green strip lists what's
                included; red strip lists what's NOT covered. Both hide when
                the corresponding list is empty so legacy plans stay tidy. */}
            {covered.length > 0 && (
              <div className="bg-emerald-50/70 border-t border-emerald-100 px-3 py-1.5" data-testid={`insurance-option-${p.id}-covered`}>
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-emerald-900">
                  {covered.map((c, i) => (
                    <span key={`c-${i}`} className="inline-flex items-start gap-1">
                      <Check className="h-3 w-3 mt-0.5 shrink-0 text-emerald-700" />
                      <span>{c}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
            {excluded.length > 0 && (
              <div className="bg-rose-50 border-t border-rose-200 px-3 py-1.5" data-testid={`insurance-option-${p.id}-excluded`}>
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-rose-900">
                  {excluded.map((c, i) => (
                    <span key={`x-${i}`} className="inline-flex items-start gap-1">
                      <X className="h-3 w-3 mt-0.5 shrink-0 text-rose-700" />
                      <span>{c}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default BookingInsurancePicker;
